// src/pages/financeOfficer/FinanceOfficerDashboard.jsx

import React, { useState, useEffect, useCallback } from "react";
import FOSidebar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import RequisitionsTable from "../../components/financeOfficer/RequisitionsTable";
import CreateRequisitionModal from "../../components/financeOfficer/CreateRequisitionModal";
import BudgetExpenditureChart from "../../components/financeOfficer/BudgetExpenditureChart";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";
import { 
  ReceiptText, Wallet, FileText, 
  PlusCircle, X, TrendingUp, Plus
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import LeaveRequestForm from "../../components/LeaveRequestForm";
import { jwtDecode } from "jwt-decode";
import { motion, AnimatePresence } from "framer-motion";

const API_URL = "https://ims.holdthechild.net/api/finance/requisitions";
const TOTAL_BUDGET = 250000;

const FinanceOfficerDashboard = () => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [allRequisitions, setAllRequisitions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [showLeaveForm, setShowLeaveForm] = useState(false);

    const token = localStorage.getItem("token");
    const decoded = token ? jwtDecode(token) : null;
    const staffId = decoded ? decoded.id : null;

    const fetchRequisitions = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const response = await axios.get(API_URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.data.success) {
                setAllRequisitions(response.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch requisitions:", error);
            setFetchError("Failed to load requisitions. Please try again.");
            toast.error("Failed to load requisitions.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchRequisitions();
    }, [fetchRequisitions]);

    const handleApproveRequisition = async (requisitionId) => {
        try {
            await axios.put(`${API_URL}/${requisitionId}/approve`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Requisition approved successfully!");
            fetchRequisitions();
        } catch (error) {
            console.error("Failed to approve requisition:", error);
            const message = error.response?.data?.error || "Approval failed. Please try again.";
            toast.error(message);
        }
    };

    const handleCreateRequisition = async (newRequisition) => {
        try {
            await axios.post(API_URL, newRequisition, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Requisition created successfully!");
            setIsCreateModalOpen(false);
            fetchRequisitions();
        } catch (error) {
            console.error("Failed to create requisition:", error);
            const message = error.response?.data?.error || "Creation failed. Please try again.";
            toast.error(message);
        }
    };

    const totalSpend = allRequisitions
        .filter(req => req.status === "Approved")
        .reduce((sum, req) => sum + req.amount, 0);

    const outstandingRequisitions = allRequisitions
        .filter(req => req.status === "Pending").length;

    const remainingBudget = TOTAL_BUDGET - totalSpend;

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen w-full bg-gray-50">
                <p className="font-black text-[10px] uppercase tracking-widest text-gray-400">Loading dashboard data...</p>
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className="flex items-center justify-center min-h-screen w-full bg-gray-50">
                <p className="font-black text-[10px] uppercase tracking-widest text-red-500">{fetchError}</p>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <FOSidebar
                visible={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-7xl mx-auto">
                        
                        <div className="flex flex-col items-center text-center mb-12">
                            <PremiumHeader 
                                title="Finance" 
                                lastWordAccent="Management" 
                                subtitle="Monitor expenditures, approvals, and budget allocations in real-time." 
                            />
                            <div className="flex flex-wrap justify-center gap-4 mt-8">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setIsCreateModalOpen(true)}
                                    className="flex items-center gap-3 bg-gray-900 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-orange-600 shadow-2xl shadow-gray-200 transition-all active:scale-95"
                                >
                                    <PlusCircle size={16} strokeWidth={3} /> Create Requisition
                                </motion.button>
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setShowLeaveForm(true)}
                                    className="flex items-center gap-3 bg-white text-gray-900 border border-gray-100 px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-gray-50 shadow-sm transition-all active:scale-95"
                                >
                                    <Plus size={16} strokeWidth={3} /> Request Absence
                                </motion.button>
                            </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                            {[
                                { title: "Total Spend", val: `$${totalSpend.toLocaleString('en-US')}`, icon: ReceiptText, accent: "orange", desc: "Approved allocations" },
                                { title: "Remaining Budget", val: `$${remainingBudget.toLocaleString('en-US')}`, icon: Wallet, accent: "emerald", desc: "Available resources" },
                                { title: "Outstanding Items", val: outstandingRequisitions, icon: FileText, accent: "blue", desc: "Awaiting approval" }
                            ].map((stat, i) => (
                                <motion.div 
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    key={i}
                                >
                                    <PremiumStatCard 
                                        title={stat.title} 
                                        value={stat.val} 
                                        icon={<stat.icon className={`text-${stat.accent}-600`} />} 
                                        iconBg={`bg-${stat.accent}-50`}
                                        description={stat.desc}
                                    />
                                </motion.div>
                            ))}
                        </div>

                        {/* Chart Section */}
                        <div className="mb-12">
                            <PremiumCard title="Budget Strategy & Expenditure Analytics" icon={<TrendingUp size={18} />}>
                                <div className="pt-4 py-8">
                                    <BudgetExpenditureChart totalBudget={TOTAL_BUDGET} totalSpend={totalSpend} />
                                </div>
                            </PremiumCard>
                        </div>

                        {/* Table Section */}
                        <div className="mb-12">
                            <PremiumCard title="Strategic Requisitions Ledger" icon={<FileText size={18} />}>
                                <div className="mt-4">
                                    <RequisitionsTable
                                        requisitions={allRequisitions}
                                        onApprove={handleApproveRequisition}
                                    />
                                </div>
                            </PremiumCard>
                        </div>
                    </div>
                </main>
            </div>

            {/* Modals */}
            <AnimatePresence>
                {isCreateModalOpen && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                        <motion.div 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }} 
                            exit={{ opacity: 0 }} 
                            onClick={() => setIsCreateModalOpen(false)} 
                            className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
                        />
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 20 }} 
                            animate={{ scale: 1, opacity: 1, y: 0 }} 
                            exit={{ scale: 0.95, opacity: 0, y: 20 }} 
                            className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-100"
                        >
                            <div className="px-10 py-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                                <div className="flex items-center gap-4">
                                    <div className="size-10 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center">
                                        <PlusCircle size={20} strokeWidth={3} />
                                    </div>
                                    <div>
                                        <h2 className="text-xs font-black text-gray-800 uppercase tracking-widest">New Requisition</h2>
                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight mt-1">Initialize a formal funding request</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setIsCreateModalOpen(false)} 
                                    className="p-3 bg-white hover:bg-gray-100 rounded-2xl transition-all active:scale-95 border border-gray-100 shadow-sm"
                                >
                                    <X size={20} className="text-gray-400" />
                                </button>
                            </div>
                            <div className="p-10 overflow-y-auto max-h-[calc(90vh-120px)] custom-scrollbar">
                                <CreateRequisitionModal
                                    onClose={() => setIsCreateModalOpen(false)}
                                    onSubmit={handleCreateRequisition}
                                />
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showLeaveForm && staffId && token && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                        <motion.div 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }} 
                            exit={{ opacity: 0 }} 
                            onClick={() => setShowLeaveForm(false)} 
                            className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
                        />
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 20 }} 
                            animate={{ scale: 1, opacity: 1, y: 0 }} 
                            exit={{ scale: 0.95, opacity: 0, y: 20 }} 
                            className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-100"
                        >
                            <div className="px-10 py-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                                <div className="flex items-center gap-4">
                                    <div className="size-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                        <Plus size={20} strokeWidth={3} />
                                    </div>
                                    <div>
                                        <h2 className="text-xs font-black text-gray-800 uppercase tracking-widest">Absence Request</h2>
                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight mt-1">Submit your request for review</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setShowLeaveForm(false)} 
                                    className="p-3 bg-white hover:bg-gray-100 rounded-2xl transition-all active:scale-95 border border-gray-100 shadow-sm"
                                >
                                    <X size={20} className="text-gray-400" />
                                </button>
                            </div>
                            <div className="p-10 overflow-y-auto max-h-[calc(90vh-120px)] custom-scrollbar">
                                <LeaveRequestForm
                                    staffId={staffId}
                                    token={token}
                                    onClose={() => setShowLeaveForm(false)}
                                />
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default FinanceOfficerDashboard;

